import { supabase } from './supabaseClient';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertUUID(value, context) {
  if (typeof value !== 'string' || !UUID_REGEX.test(value)) {
    throw new Error(`${context} : valeur invalide (UUID attendu, reçu ${typeof value}: ${JSON.stringify(value).slice(0, 80)})`);
  }
}

// Fetch active diagnostic questions ordered by display_order
export async function fetchDiagnosticQuestions() {
  const { data, error } = await supabase
    .from('diagnostic_questions')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true });

  if (error) throw new Error(`Erreur lors du chargement des questions: ${error.message}`);
  return data;
}

// Create a new diagnostic session via RPC — always returns a UUID string
export async function createDiagnosticSession(companyId) {
  assertUUID(companyId, 'createDiagnosticSession(companyId)');

  const { data, error } = await supabase.rpc('create_diagnostic_session', {
    p_company_id: companyId,
  });

  if (error) throw new Error(`Erreur lors de la création de la session: ${error.message}`);

  // Extract UUID: RPC may return "uuid-string" or { id: "uuid-string", ... }
  const sessionId = typeof data === 'string' ? data : data?.id;
  assertUUID(sessionId, 'createDiagnosticSession retour');
  return sessionId;
}

// Upsert a diagnostic answer via RPC
export async function upsertDiagnosticAnswer({ diagnosticSessionId, questionId, answerValue, answerLabel, scoreValue }) {
  assertUUID(diagnosticSessionId, 'upsertDiagnosticAnswer(diagnosticSessionId)');
  assertUUID(questionId, 'upsertDiagnosticAnswer(questionId)');

  const { data, error } = await supabase.rpc('upsert_diagnostic_answer', {
    p_diagnostic_session_id: diagnosticSessionId,
    p_question_id: questionId,
    p_answer_value: answerValue,
    p_answer_label: answerLabel || null,
    p_score_value: scoreValue ?? null,
  });

  if (error) throw new Error(`Erreur lors de l'enregistrement de la réponse: ${error.message}`);
  return data;
}

// Submit a completed diagnostic session via RPC
export async function submitDiagnosticSession(sessionId) {
  assertUUID(sessionId, 'submitDiagnosticSession(sessionId)');

  const { data, error } = await supabase.rpc('submit_diagnostic_session', {
    p_diagnostic_session_id: sessionId,
  });

  if (error) throw new Error(`Erreur lors de la soumission du diagnostic: ${error.message}`);
  return data;
}

// Call the Edge Function to generate the AI report
export async function generateAIReport(sessionId) {
  assertUUID(sessionId, 'generateAIReport(sessionId)');

  const { data, error } = await supabase.functions.invoke('generate-ai-report', {
    body: { diagnostic_session_id: sessionId },
  });

  if (error) throw new Error(`Erreur lors de la génération du rapport IA: ${error.message}`);
  return data;
}

// Fetch diagnostic scores for a session (single row)
export async function fetchDiagnosticScores(sessionId) {
  assertUUID(sessionId, 'fetchDiagnosticScores(sessionId)');

  const { data, error } = await supabase
    .from('diagnostic_scores')
    .select('id, diagnostic_session_id, finance_score, time_score, digital_score, strategy_score, global_score, maturity_level, priority_1, priority_2, priority_3, score_details, created_at')
    .eq('diagnostic_session_id', sessionId)
    .limit(1)
    .single();

  if (error) throw new Error(`Erreur lors du chargement des scores: ${error.message}`);
  return data;
}

// Fetch AI report for a session (direct columns)
export async function fetchAIReport(sessionId) {
  assertUUID(sessionId, 'fetchAIReport(sessionId)');

  const { data, error } = await supabase
    .from('ai_reports')
    .select('id, diagnostic_session_id, prompt_version, model_name, summary, strengths, weaknesses, recommendations, action_plan_30_days, action_plan_90_days, recommended_services, created_at')
    .eq('diagnostic_session_id', sessionId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) throw new Error(`Erreur lors du chargement du rapport: ${error.message}`);
  return data;
}

// Fetch user's diagnostic sessions with company join
export async function fetchUserSessions(userId) {
  const { data, error } = await supabase
    .from('diagnostic_sessions')
    .select('*, companies(company_name, industry)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Erreur lors du chargement des sessions: ${error.message}`);
  return data;
}

// Fetch user's company
export async function fetchUserCompany(userId) {
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .eq('user_id', userId)
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw new Error(`Erreur lors du chargement de l'entreprise: ${error.message}`);
  }
  return data;
}

// Create a company for the user
export async function createCompany({ userId, companyName, industry }) {
  const { data, error } = await supabase
    .from('companies')
    .insert({ user_id: userId, company_name: companyName, industry: industry || null })
    .select()
    .single();

  if (error) throw new Error(`Erreur lors de la création de l'entreprise: ${error.message}`);
  return data;
}

// Fetch user profile from users table
export async function fetchUserProfile(userId) {
  const { data, error } = await supabase
    .from('users')
    .select('id, email, phone, first_name, last_name')
    .eq('id', userId)
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw new Error(`Erreur lors du chargement du profil: ${error.message}`);
  }
  return data;
}

// Update user phone in users table
export async function updateUserPhone(userId, phone) {
  const { data, error } = await supabase
    .from('users')
    .update({ phone })
    .eq('id', userId)
    .select()
    .single();

  if (error) throw new Error(`Erreur lors de la mise à jour du téléphone: ${error.message}`);
  return data;
}

// Update company profile (industry, size_range, city)
export async function updateCompanyProfile(companyId, updates) {
  const allowedFields = ['industry', 'size_range', 'city'];
  const filteredUpdates = {};
  for (const key of allowedFields) {
    if (updates[key] !== undefined) {
      filteredUpdates[key] = updates[key];
    }
  }

  const { data, error } = await supabase
    .from('companies')
    .update(filteredUpdates)
    .eq('id', companyId)
    .select()
    .single();

  if (error) throw new Error(`Erreur lors de la mise à jour de l'entreprise: ${error.message}`);
  return data;
}

// Create empty company for user if none exists
export async function getOrCreateCompany(userId) {
  // First try to fetch existing company
  let { data: company, error } = await supabase
    .from('companies')
    .select('*')
    .eq('user_id', userId)
    .limit(1)
    .single();

  // If no company exists (PGRST116 = no rows), create one
  if (error && error.code === 'PGRST116') {
    const { data: newCompany, error: createError } = await supabase
      .from('companies')
      .insert({ user_id: userId, company_name: 'Mon entreprise' })
      .select()
      .single();

    if (createError) throw new Error(`Erreur lors de la création de l'entreprise: ${createError.message}`);
    return newCompany;
  }

  if (error) throw new Error(`Erreur lors du chargement de l'entreprise: ${error.message}`);
  
  // Fallback: if company is null for any reason, create one
  if (!company) {
    const { data: newCompany, error: createError } = await supabase
      .from('companies')
      .insert({ user_id: userId, company_name: 'Mon entreprise' })
      .select()
      .single();

    if (createError) throw new Error(`Erreur lors de la création de l'entreprise: ${createError.message}`);
    return newCompany;
  }
  
  return company;
}



// Fetch generated PDF file for a session
export async function fetchGeneratedPDF(diagnosticSessionId) {
  const { data, error } = await supabase
    .from('generated_files')
    .select('id, file_name, storage_path, file_type, mime_type, created_at, is_active')
    .eq('diagnostic_session_id', diagnosticSessionId)
    .eq('file_type', 'pdf_report')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw new Error(`Erreur lors de la récupération du fichier: ${error.message}`);
  }
  return data;
}

// Get signed URL for downloading a file from Storage
export async function getSignedDownloadUrl(storagePath) {
  const { data, error } = await supabase.storage
    .from('reports')
    .createSignedUrl(storagePath, 3600); // 1 hour validity

  if (error) {
    throw new Error(`Erreur lors de la génération du lien: ${error.message}`);
  }
  return data.signedUrl;
}


// Generate PDF report via Edge Function
export async function generatePDF(sessionId) {
  console.log("=== GENERATE PDF DEBUG ===");
  console.log("Step 1: Session ID:", sessionId);
  
  // 1. Récupérer la session utilisateur
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  console.log("Step 2: Session error:", sessionError);
  console.log("Step 3: Has access_token:", !!session?.access_token);
  console.log("Step 4: User ID:", session?.user?.id);

  if (sessionError) {
    console.error("Auth session error:", sessionError);
    throw new Error(`Erreur d'authentification: ${sessionError.message}`);
  }
  
  if (!session?.access_token) {
    console.error("No access token found");
    throw new Error("Utilisateur non authentifié - pas de token");
  }

  console.log("Step 5: Calling Edge Function...");
  console.log("Step 5b: Token prefix:", session.access_token.substring(0, 20) + "...");

  // 2. Appel de la Edge Function AVEC token
  const { data, error } = await supabase.functions.invoke(
    "generate-report-pdf",
    {
      body: {
        diagnostic_session_id: sessionId,
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    }
  );

  console.log("Step 6: Edge Function response data:", data);
  console.log("Step 6b: Edge Function error:", error);

  // =====================================================
  // LOGIQUE DE SUCCÈS AMÉLIORÉE
  // On considère le succès si on a une download_url, 
  // même si Supabase retourne une "erreur" (timeout, etc.)
  // =====================================================

  // Cas 1: Réponse avec download_url = SUCCÈS (priorité absolue)
  if (data?.download_url) {
    console.log("Step 7: PDF SUCCESS! URL:", data.download_url);
    console.log("Step 7b: Email status:", data.email_status);
    return {
      success: true,
      download_url: data.download_url,
      file_name: data.file_name || "rapport-finoveo.pdf",
      storage_path: data.storage_path,
      email_status: data.email_status || "unknown",
    };
  }

  // Cas 2: Pas de data mais pas d'erreur grave = probablement OK (emails envoyés)
  // L'Edge Function peut avoir un timeout mais avoir quand même fonctionné
  if (error && error.message?.includes("FunctionsFetchError")) {
    console.log("Step 7: Network/timeout error but function may have succeeded");
    // On ne throw pas d'erreur - on retourne un succès partiel
    return {
      success: true,
      download_url: null,
      email_status: "sent", // On suppose que ça a marché si on arrive ici
      _noDownloadUrl: true,
    };
  }

  // Cas 3: Erreur explicite de l'Edge Function
  if (error) {
    console.error("=== EDGE FUNCTION ERROR DETAILS ===");
    console.error("Error name:", error.name);
    console.error("Error message:", error.message);
    console.error("Error context:", error.context);
    
    // Si l'erreur contient des détails sur un succès partiel, on ne throw pas
    if (error.context?.body?.success || error.context?.body?.download_url) {
      const body = error.context.body;
      return {
        success: true,
        download_url: body.download_url,
        email_status: body.email_status || "unknown",
      };
    }
    
    const errorMessage = error.message || "Erreur inconnue";
    throw new Error(`Erreur PDF: ${errorMessage}`);
  }

  // Cas 4: Pas de data
  if (!data) {
    console.error("No data returned from Edge Function");
    throw new Error("Aucune donnée retournée par la fonction");
  }

  // Cas 5: Data avec erreur explicite
  if (data.error) {
    console.error("Step 7: Edge Function returned error:", data.error);
    throw new Error(data.details || data.error);
  }

  // Cas 6: Réponse inattendue mais success=true
  if (data.success === true) {
    console.log("Step 7: Success but no download_url");
    return {
      success: true,
      download_url: null,
      email_status: data.email_status || "sent",
      _noDownloadUrl: true,
    };
  }

  // Cas 7: Réponse totalement inattendue
  console.error("Step 7: Unexpected response format:", data);
  throw new Error("Réponse inattendue de la fonction PDF");
}
